/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { Maybe } from '@amazon/vinyl-util'

/**
 * Extracts the in-app path (pathname only) from a server-provided
 * canonical `url` — e.g.
 * `https://music.amazon.com/podcasts/<uuid>/startalk-radio?ref=...` →
 * `/podcasts/<uuid>/startalk-radio`. The embedded SEO slug rides along
 * in the pathname, so link builders get a slugged href straight from
 * the wire instead of client-side slugifying a title (matches green
 * hornet's `getPathFromURL`).
 *
 * Host, query, and hash are dropped. Bare paths (already
 * relative) round-trip through the parser via a dummy base. Returns
 * `null` for missing / unparseable input so callers can fall back to an
 * id-only path.
 */
export function getPathFromURL(url: Maybe<string>): string | null {
    if (!url) return null
    try {
        return new URL(url, 'https://music.amazon.com').pathname
    } catch {
        return null
    }
}

/** GH's `slugify`: lowercase, drop punctuation, and join words with single hyphens. */
export function slugify(text: Maybe<string>): string {
    if (!text) return ''
    return text
        .toLowerCase()
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/[^a-z0-9-]/g, '-')
}

interface CatalogLinkFields {
    readonly id?: Maybe<string>
    readonly url?: Maybe<string>
}

/** An album's page: the catalog URL's path, else `/albums/:id` (GH's `getPathForAlbum`). */
export function albumPath(album: Maybe<CatalogLinkFields>): string | null {
    return getPathFromURL(album?.url) ?? (album?.id ? `/albums/${album.id}` : null)
}

/** An artist's page: the catalog URL's path, else `/artists/:id/<name-slug>` (GH's `getPathForArtist`). */
export function artistPath(
    artist: Maybe<CatalogLinkFields & { readonly name?: Maybe<string> }>
): string | null {
    const fromUrl = getPathFromURL(artist?.url)
    if (fromUrl) return fromUrl
    if (!artist?.id) return null
    const slug = slugify(artist.name)
    return `/artists/${artist.id}${slug ? `/${slug}` : ''}`
}

/** An audiobook's page: the catalog URL's path, else `/audiobooks/:id` (GH's `getPathForAudiobook`). */
export function audiobookPath(audiobook: Maybe<CatalogLinkFields>): string | null {
    return getPathFromURL(audiobook?.url) ?? (audiobook?.id ? `/audiobooks/${audiobook.id}` : null)
}

/**
 * Keeps an INTERNAL `music.amazon.*` card link on the current origin. Firefly
 * hands back absolute prod links (`https://music.amazon.com/albums/…`); on a
 * dev / self-host origin (local.music.amazon.com, localhost, amp.music.amazon.dev)
 * following one leaves the app. Rewriting a `music.amazon.<tld>` host to a bare
 * path keeps it same-origin so the in-app router handles it. Genuinely external
 * links (ticket vendors, non-music domains) pass through untouched.
 */
export function currentHostHref(rawUrl: string): string {
    if (!rawUrl) return rawUrl
    let parsed: URL
    try {
        parsed = new URL(rawUrl)
    } catch {
        // Relative / malformed: already same-origin, or uncorrectable — leave as-is.
        return rawUrl
    }
    if (parsed.hostname.startsWith('music.amazon.')) {
        return parsed.pathname + parsed.search + parsed.hash
    }
    return rawUrl
}

/** The in-app path of a link to a Music web host (`music.amazon.*`, `local.music.amazon.*`), else `null` for an external link. */
export function musicInternalPath(rawUrl: string): string | null {
    try {
        const { hostname, pathname, search, hash } = new URL(rawUrl, 'https://music.amazon.com')
        const internal = /^(local\.)?music\.amazon\./.test(hostname)
        return internal ? pathname + search + hash : null
    } catch {
        return null
    }
}
